import { useMemo } from "react";

import { getValueByPath } from "utils/get-value-by-path";
import { Report, ReportCondition, ReportContext } from "store/types";

function getContextField(context: ReportContext, field: string): unknown {
  return getValueByPath(context, field);
}

/**
 * Evaluates a report condition against the provided context.
 * Exported for use in client trigger evaluation.
 */
export const evaluateCondition = (
  condition: ReportCondition,
  context: ReportContext
): boolean => {
  switch (condition.condition) {
    case "AND":
      if (!("rules" in condition)) return true;
      return condition.rules.every((rule) => evaluateCondition(rule, context));

    case "OR":
      if (!("rules" in condition)) return true;
      return condition.rules.some((rule) => evaluateCondition(rule, context));

    case "NOT":
      if (!("rule" in condition)) return true;
      return !evaluateCondition(condition.rule, context);

    case "context_field_equals": {
      const fieldValue = getContextField(context, condition.field as string);
      const comparisonValue = condition.value;
      return fieldValue === comparisonValue;
    }

    case "context_field_not_equals": {
      const fieldValue = getContextField(context, condition.field as string);
      const comparisonValue = condition.value;
      return fieldValue !== comparisonValue;
    }

    case "context_field_in": {
      const fieldValue = getContextField(context, condition.field as string);
      if (fieldValue === undefined) return false;
      const primitive =
        typeof fieldValue === "object" && fieldValue !== null
          ? undefined
          : (fieldValue as string | number | boolean | null);
      if (primitive === undefined) return false;
      return condition.values?.includes(primitive) ?? false;
    }

    case "context_field_contains": {
      const fieldValue = getContextField(context, condition.field as string);
      const needle = condition.value != null ? String(condition.value) : "";
      const result =
        fieldValue !== undefined &&
        fieldValue !== null &&
        needle !== "" &&
        String(fieldValue).includes(needle);
      return result;
    }

    case "context_field_array_length_gte": {
      const fieldValue = getContextField(context, condition.field as string);
      const minLength =
        typeof condition.value === "number"
          ? condition.value
          : Number(condition.value);
      const result =
        Array.isArray(fieldValue) &&
        Number.isFinite(minLength) &&
        fieldValue.length >= minLength;
      return result;
    }

    case "eval":
      // For eval conditions, you could implement JavaScript evaluation
      // For now, return true as a placeholder
      console.warn("eval condition not implemented", condition);
      return true;

    case "has_single_investor":
      // Check if deal has a single investor
      // This is typically a backend check, but we can check context for investor_count
      // For now, return true to allow the report (backend will validate)
      if (condition.deal_id_field && context[condition.deal_id_field]) {
        // If investor_count is provided in context, use it
        if (typeof context.investor_count === "number") {
          return context.investor_count === 1;
        }
        // Otherwise, default to true (backend will handle the actual check)
        return true;
      }
      return true;

    case "user_has_role":
    case "user_has_permission":
    case "account_has_feature":
      // These require additional context (user info, account info, etc.)
      // For now, return true as a placeholder
      console.warn(
        `Condition type "${condition.condition}" not implemented yet`
      );
      return true;

    default:
      return true;
  }
};

/**
 * Filters reports based on their conditions_schema
 */
const filterReportsByConditions = (
  reports: Report[],
  context: ReportContext
): Report[] => {
  return reports.filter((report) => {
    // If no conditions, include the report
    if (!report.configuration?.conditions_schema) {
      return true;
    }

    // Evaluate the condition
    return evaluateCondition(report.configuration.conditions_schema, context);
  });
};

/**
 * Hook to get and filter reports for a report group
 * Filters reports client-side based on conditions and row context
 */
export const useFilteredReports = (
  groupName: string,
  rowContext: ReportContext,
  getReportsByGroup?: (groupName: string) => Report[]
) => {
  // Get all reports for this group and filter by conditions
  const reports = useMemo(() => {
    if (!getReportsByGroup) {
      return [];
    }

    const allReports = getReportsByGroup(groupName);

    // Filter reports based on conditions if rowContext is provided
    if (rowContext && Object.keys(rowContext).length) {
      return filterReportsByConditions(allReports, rowContext);
    }

    return allReports;
  }, [getReportsByGroup, groupName, rowContext]);

  return { reports };
};
